import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  switchMap,
} from 'rxjs';
import { HrAlertService, HrHttpService } from '../../hr-http.service';
import { EmployeeProfileService } from '../employee-profile.service';
import { NgxSpinnerService } from 'ngx-spinner';

type CampusOption = { campusId: string; campusName: string };
type BuildingOption = {
  buildingId: string;
  buildingName: string;
  fusionCode: string;
  label: string;
};

const EMPTY_PROFILE = {
  payrollId: '',
  employeeName: '',
  designation: '',
  mobileNo: '',
  campusName: '',
  buildingName: '',
  dateOfJoining: '',
  subject: '',
  department: '',
  managerName: '',
  dateOfBirth: '',
  gender: '',
  stateName: '',
  cityName: '',
};

@Component({
  selector: 'app-employee-profile-form',
  templateUrl: './employee-profile-form.component.html',
  styleUrls: ['./employee-profile-form.component.scss'],
})
export class EmployeeProfileFormComponent implements OnDestroy {
  private static readonly MIN_SEARCH_LENGTH = 5;

  readonly profileFields = [
    { key: 'payrollId', label: 'Payroll Id', id: 'epf-payroll' },
    { key: 'employeeName', label: 'Employee Name', id: 'epf-name' },
    { key: 'designation', label: 'Designation', id: 'epf-desig' },
    { key: 'mobileNo', label: 'Mobile', id: 'epf-mobile' },
    { key: 'dateOfJoining', label: 'Date of Joining', id: 'epf-doj' },
    { key: 'subject', label: 'Subject', id: 'epf-subject' },
    { key: 'department', label: 'Department', id: 'epf-dept' },
    { key: 'managerName', label: 'Manager Name', id: 'epf-manager' },
    { key: 'dateOfBirth', label: 'Date of Birth', id: 'epf-dob' },
    { key: 'gender', label: 'Gender', id: 'epf-gender' },
  ] as const;

  readonly campusFields = [
    { key: 'stateName', label: 'State', id: 'epf-state' },
    { key: 'cityName', label: 'City', id: 'epf-city' },
  ] as const;

  searchControl = new FormControl<string | Record<string, unknown>>('');
  campusSearch = new FormControl('');
  buildingSearch = new FormControl('');

  profileForm = new FormGroup(
    Object.fromEntries(
      Object.keys(EMPTY_PROFILE).map((key) => [key, new FormControl({ value: '', disabled: true })])
    )
  );

  private readonly optionsSubject = new BehaviorSubject<Record<string, unknown>[]>([]);
  employeeOptions = this.optionsSubject.asObservable();
  private optionsList: Record<string, unknown>[] = [];

  selectedProfile: Record<string, unknown> | null = null;
  hasSearched = false;
  employeeDetailsLoaded = false;
  employeeDetailsAvailable = false;
  profileImageUrl: string | null = null;
  campusList: CampusOption[] = [];
  filteredCampusList: CampusOption[] = [];
  buildingList: BuildingOption[] = [];
  filteredBuildingList: BuildingOption[] = [];
  buildingDropdownActive = false;
  loadingBuildings = false;
  currentCampusId = '';
  pendingBuildingLabel = '';
  originalBuildingId = '';
  originalBuildingLabel = '';
  savingBuilding = false;

  @ViewChild('buildingDropdownHost') buildingDropdownHost?: ElementRef<HTMLElement>;

  private readonly subs = new Subscription();
  private profileImageObjectUrl: string | null = null;

  constructor(
    private readonly hrHttp: HrHttpService,
    private readonly hrAlert: HrAlertService,
    private readonly service: EmployeeProfileService,
    private readonly spinner: NgxSpinnerService
  ) {
    this.subs.add(this.buildSearchStream().subscribe());
    this.subs.add(this.campusSearch.valueChanges.subscribe((v) => this.filterCampuses(v ?? '')));
    this.subs.add(this.buildingSearch.valueChanges.subscribe((v) => this.filterBuildings(v ?? '')));
  }

  ngOnDestroy(): void {
    this.spinner.hide();
    this.subs.unsubscribe();
    this.revokeProfileImage();
  }

  get showNoEmployee(): boolean {
    return !this.selectedProfile && !this.hasSearched;
  }

  get showNoSearchResults(): boolean {
    return this.hasSearched && !this.selectedProfile && this.optionsList.length === 0;
  }

  get showNoEmployeeDetails(): boolean {
    return !!this.selectedProfile && this.employeeDetailsLoaded && !this.employeeDetailsAvailable;
  }

  get showProfileSections(): boolean {
    return !!this.selectedProfile && !this.showNoEmployeeDetails;
  }

  get emptyStateMessage(): string {
    if (this.showNoSearchResults) return 'No Data Found';
    if (this.showNoEmployeeDetails) return 'No Employee Details';
    return 'No Employee';
  }

  get showEmptyState(): boolean {
    return this.showNoEmployee || this.showNoSearchResults || this.showNoEmployeeDetails;
  }

  displayEmployeeLabel = (item: Record<string, unknown> | string | null): string => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return this.getOptionLabel(item);
  };

  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as Record<string, unknown>;
    if (item && typeof item === 'object') this.applyProfile(item);
  }

  clearProfile(): void {
    this.spinner.hide();
    this.selectedProfile = null;
    this.employeeDetailsLoaded = false;
    this.employeeDetailsAvailable = false;
    this.hasSearched = false;
    this.resetCampuses();
    this.revokeProfileImage();
    this.searchControl.setValue('', { emitEvent: false });
    this.profileForm.reset({ ...EMPTY_PROFILE });
    this.optionsList = [];
    this.optionsSubject.next([]);
  }

  trackByPayrollId = (_i: number, item: Record<string, unknown>): string =>
    this.getLookupId(item) || String(_i);

  getOptionPayrollId(item: Record<string, unknown>): string {
    return this.getLookupId(item);
  }

  getOptionName(item: Record<string, unknown>): string {
    return this.pick(item, 'empName', 'employeeName', 'firstName', 'userName', 'name')
      .replace(/\b\d{10}\b/g, '')
      .replace(/\s*\|\s*/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  getOptionLabel(item: Record<string, unknown>): string {
    const id = this.getLookupId(item);
    const name = this.getOptionName(item);
    return id && name ? `${id} - ${name}` : id || name || 'Employee';
  }

  onCampusChange(campusName: string): void {
    if (!campusName) return;
    this.closeBuildingChangePanel();
    this.currentCampusId =
      this.campusList.find((c) => c.campusName === campusName)?.campusId ?? '';
    this.setControlEnabled('buildingName', false);
  }

  get canSaveBuildingChange(): boolean {
    const selected = String(this.pendingBuildingLabel ?? '').trim();
    return (
      this.buildingDropdownActive &&
      this.buildingList.length > 0 &&
      !!selected &&
      selected !== this.originalBuildingLabel &&
      !this.savingBuilding &&
      !this.loadingBuildings
    );
  }

  get isChangeBuildingDisabled(): boolean {
    return this.loadingBuildings || this.savingBuilding || !this.currentCampusId;
  }

  get isSaveBuildingDisabled(): boolean {
    return !this.canSaveBuildingChange;
  }

  onChangeBuildingClick(): void {
    this.openBuildingChange();
  }

  onSaveBuildingClick(): void {
    this.saveEmployeeChangeBuilding();
  }

  onPendingBuildingChange(label: string): void {
    this.pendingBuildingLabel = String(label ?? '').trim();
  }

  closeBuildingChangePanel(): void {
    this.buildingDropdownActive = false;
    this.buildingList = [];
    this.filteredBuildingList = [];
    this.pendingBuildingLabel = '';
    this.buildingSearch.setValue('', { emitEvent: false });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.buildingDropdownActive) return;

    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.buildingDropdownHost?.nativeElement.contains(target)) return;

    this.closeBuildingChangePanel();
  }

  private openBuildingChange(): void {
    if (!this.currentCampusId) {
      this.hrAlert.show('Campus not found for this employee.', 'Error');
      return;
    }

    this.loadingBuildings = true;
    this.spinner.show();
    this.loadBuildingsForCampusId(
      this.currentCampusId,
      this.originalBuildingLabel || String(this.profileForm.get('buildingName')?.value ?? '').trim(),
      this.originalBuildingId
    );
  }

  saveEmployeeChangeBuilding(): void {
    if (!this.canSaveBuildingChange) return;

    const selectedLabel = String(this.pendingBuildingLabel ?? '').trim();
    const selected = this.buildingList.find((b) => b.label === selectedLabel);
    const changeBuildingId = selected?.buildingId ?? '';

    if (!changeBuildingId) {
      this.hrAlert.show('Please select a valid building.', 'Error');
      return;
    }

    const payrollId = String(this.profileForm.get('payrollId')?.value ?? '').trim();
    if (!payrollId) {
      this.hrAlert.show('Payroll Id not found. Please search employee again.', 'Error');
      return;
    }

    const payload = {
      changeBuildingId,
      payrollId,
      buildingName: selected?.buildingName ?? '',
    };

    this.savingBuilding = true;
    this.spinner.show();
    this.subs.add(
      this.service.saveEmployeeChangeBuilding(changeBuildingId, payrollId, payload).pipe(
        finalize(() => {
          this.savingBuilding = false;
          this.spinner.hide();
        })
      ).subscribe({
        next: () => {
          // Keep saved building in UI immediately (GET may still return old value briefly)
          this.applyCurrentBuildingToForm(changeBuildingId, selectedLabel, payrollId);
          this.closeBuildingChangePanel();
          this.hrAlert.show('Building changed successfully.', 'Success');
          // Refresh from GET after short delay so backend can persist
          this.refreshCurrentBuildingAfterSave(payrollId, changeBuildingId, selectedLabel);
        },
        error: () => {
          this.hrAlert.show('Unable to change building. Please try again.', 'Error');
        },
      })
    );
  }

  private applyCurrentBuildingToForm(
    buildingId: string,
    displayName: string,
    payrollId: string
  ): void {
    this.originalBuildingId = buildingId;
    this.originalBuildingLabel = displayName;
    this.profileForm.get('buildingName')?.setValue(displayName, { emitEvent: false });
    this.storeSavedBuilding(payrollId, buildingId, displayName);
  }

  private refreshCurrentBuildingAfterSave(
    payrollId: string,
    savedBuildingId: string,
    savedLabel: string
  ): void {
    this.subs.add(
      of(null).pipe(delay(800)).subscribe(() => {
        this.loadCurrentBuildingDetails(payrollId, {
          keepIfStale: true,
          fallbackId: savedBuildingId,
          fallbackLabel: savedLabel,
        });
      })
    );
  }

  private savedBuildingStorageKey(payrollId: string): string {
    return 'epf_saved_building_' + String(payrollId || '').trim().toUpperCase();
  }

  private storeSavedBuilding(payrollId: string, buildingId: string, label: string): void {
    if (!payrollId || !buildingId) return;
    try {
      localStorage.setItem(
        this.savedBuildingStorageKey(payrollId),
        JSON.stringify({ buildingId, label })
      );
    } catch {
      /* ignore storage errors */
    }
  }

  private readSavedBuilding(payrollId: string): { buildingId: string; label: string } {
    if (!payrollId) return { buildingId: '', label: '' };
    try {
      const raw = localStorage.getItem(this.savedBuildingStorageKey(payrollId));
      if (!raw) return { buildingId: '', label: '' };
      const parsed = JSON.parse(raw) as { buildingId?: string; label?: string };
      return {
        buildingId: String(parsed?.buildingId ?? '').trim(),
        label: String(parsed?.label ?? '').trim(),
      };
    } catch {
      return { buildingId: '', label: '' };
    }
  }

  onCampusDropdownOpened(opened: boolean): void {
    this.onSearchableOpened(opened, this.campusSearch, () => {
      this.filteredCampusList = [...this.campusList];
      this.positionSelectPanel('epf-campus', 'epf-campus-panel');
    });
  }

  onBuildingDropdownOpened(opened: boolean): void {
    this.onSearchableOpened(opened, this.buildingSearch, () => {
      this.filteredBuildingList = [...this.buildingList];
      this.positionSelectPanel('epf-buildings-select', 'epf-building-panel');
    });
  }

  private onSearchableOpened(opened: boolean, search: FormControl, onOpen: () => void): void {
    if (!opened) return;
    search.setValue('', { emitEvent: false });
    setTimeout(onOpen);
  }

  private positionSelectPanel(triggerId: string, panelClass: string): void {
    const trigger = document.getElementById(triggerId);
    const panel = document.querySelector(`.${panelClass}`) as HTMLElement | null;
    const pane = panel?.closest('.cdk-overlay-pane') as HTMLElement | null;
    if (!trigger || !pane || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const maxPanelHeight = 260;
    const minPanelHeight = 120;
    const panelHeight = Math.min(panel.scrollHeight || maxPanelHeight, maxPanelHeight);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < panelHeight && spaceAbove >= spaceBelow;
    const available = openUp ? spaceAbove : spaceBelow;
    const height = Math.min(maxPanelHeight, Math.max(minPanelHeight, available));
    const top = openUp
      ? Math.max(gap, rect.top - height - gap)
      : rect.bottom + gap;

    panel.style.maxHeight = `${height}px`;
    Object.assign(pane.style, {
      top: `${top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      transform: 'none',
      position: 'fixed',
    });
  }

  private buildSearchStream(): Observable<Record<string, unknown>[]> {
    return this.searchControl.valueChanges.pipe(
      map((value) => this.getSearchText(value)),
      distinctUntilChanged(),
      debounceTime(300),
      filter((search) => {
        if (!search) {
          this.optionsList = [];
          this.optionsSubject.next([]);
          return false;
        }
        if (search.length < EmployeeProfileFormComponent.MIN_SEARCH_LENGTH) {
          this.optionsSubject.next(this.filterLocal(search));
          return false;
        }
        return true;
      }),
      switchMap((search) =>
        this.service.getEmployeeSearch(search).pipe(
          map((res) => this.hrHttp.normalizeLeftEmpSearchResults(res)),
          catchError(() => {
            this.hrAlert.show('Unable to search employees. Please try again.', 'Error');
            return of(this.filterLocal(search));
          })
        )
      ),
      map((list) => {
        this.optionsList = list;
        this.optionsSubject.next(list);
        this.hasSearched = true;
        return list;
      })
    );
  }

  private applyProfile(item: Record<string, unknown>): void {
    const payrollId = this.getLookupId(item);
    const uniqueKey = this.pick(item, 'uniqueKey', 'uniquekey');
    const mapped = this.hrHttp.mapLeftEmpSearchToViewData(item, payrollId);
    this.selectedProfile = mapped;
    this.employeeDetailsLoaded = false;
    this.employeeDetailsAvailable = false;

    this.profileForm.patchValue({
      ...EMPTY_PROFILE,
      payrollId,
      employeeName: this.pick(mapped, 'replacementEmpName', 'empName', 'employeeName', 'userName', 'name'),
      designation: this.pick(mapped, 'replacementDesigName', 'desigName', 'designation', 'designationName'),
      mobileNo: this.maskMobileNumber(
        this.pick(mapped, 'mobileNo', 'mobileNumber', 'mobile')
      ),
    });

    this.loadProfileImage(payrollId);
    this.loadEmployeeDetails(uniqueKey);
    this.loadCurrentCampus(payrollId);
  }

  private loadEmployeeDetails(encryptedId: string): void {
    if (!encryptedId) {
      this.employeeDetailsLoaded = true;
      this.employeeDetailsAvailable = false;
      return;
    }
    this.resetCampuses();
    this.employeeDetailsLoaded = false;
    this.employeeDetailsAvailable = false;
    this.subs.add(
      this.service.getEmployeeDetails(encryptedId).pipe(
        finalize(() => {
          this.employeeDetailsLoaded = true;
        })
      ).subscribe({
        next: (res) => {
          const root = this.unwrapEmpDetails(res);
          const basic = this.asRecord(root['basicInfo']);
          const campus = this.asRecord(root['currentCampusDetails']);
          const dash = this.asRecord(root['employeeDashBoardInfo']);
          const manager = this.asRecord(root['managerDetails']);
          const personal = this.asRecord(root['personalDetails']);

          this.employeeDetailsAvailable = this.hasEmpDetailsData(
            root,
            basic,
            campus,
            dash,
            manager,
            personal
          );
          if (!this.employeeDetailsAvailable) return;

          const patch: Record<string, string> = {
            dateOfJoining: this.pick(basic, 'doj', 'dateOfJoining', 'joiningDate'),
            subject: this.pick(basic, 'subject', 'subjectName', 'subjName'),
            department: this.pick(basic, 'departmentName', 'department', 'deptName', 'dept'),
            designation:
              this.pick(basic, 'designation', 'desigName', 'designationName') ||
              this.pick(dash, 'designation', 'desigName'),
            managerName: this.pick(manager, 'managerName', 'manager', 'reportingManagerName'),
            dateOfBirth: this.pick(personal, 'dob', 'dateOfBirth', 'birthDate'),
            gender: this.pick(personal, 'gender', 'employeeGender', 'sex'),
            stateName: this.pick(campus, 'stateName', 'state'),
            cityName: this.pick(campus, 'cityName', 'city'),
            employeeName: this.pick(dash, 'empName', 'employeeName', 'userName', 'name'),
            payrollId: this.pick(dash, 'payrollId', 'payRollId'),
            mobileNo: this.pick(
              personal,
              'contactNo',
              'mobileNo',
              'mobileNumber',
              'mobile'
            ),
          };

          const next: Record<string, string> = {};
          for (const [key, value] of Object.entries(patch)) {
            if (!value) continue;
            const displayValue = key === 'mobileNo' ? this.maskMobileNumber(value) : value;
            const current = String(this.profileForm.get(key)?.value ?? '').trim();
            if (!current) next[key] = displayValue;
            if (
              [
                'dateOfJoining',
                'subject',
                'department',
                'managerName',
                'dateOfBirth',
                'gender',
                'stateName',
                'cityName',
                'mobileNo',
              ].includes(key)
            ) {
              next[key] = displayValue;
            }
          }
          this.profileForm.patchValue(next);
        },
        error: () => {
          this.employeeDetailsAvailable = false;
        },
      })
    );
  }

  private hasEmpDetailsData(
    root: Record<string, unknown>,
    basic: Record<string, unknown>,
    campus: Record<string, unknown>,
    dash: Record<string, unknown>,
    manager: Record<string, unknown>,
    personal: Record<string, unknown>
  ): boolean {
    const blocks = [root, basic, campus, dash, manager, personal];
    return blocks.some((block) =>
      Object.values(block).some((val) => {
        if (val == null) return false;
        const text = String(val).trim();
        return text !== '' && text !== '-' && text !== '0';
      })
    );
  }

  /** GET getCampusbyPayrollID — fill campus name/id (not from empDetails). */
  private loadCurrentCampus(payrollId: string): void {
    if (!payrollId) {
      this.campusList = [];
      this.filteredCampusList = [];
      this.currentCampusId = '';
      this.profileForm.patchValue({ campusName: '' });
      return;
    }

    this.subs.add(
      this.service.getCurrentCampus(payrollId).subscribe({
        next: (res) => {
          const options = this.parseCurrentCampusResponse(res);
          this.campusList = options;
          this.filteredCampusList = [...options];
          this.setControlEnabled('campusName', options.length > 1);

          const selected = options[0];
          this.currentCampusId = selected?.campusId ?? '';
          this.profileForm.patchValue({ campusName: selected?.campusName ?? '' });

          this.loadCurrentBuildingDetails(payrollId);
        },
        error: () => {
          this.campusList = [];
          this.filteredCampusList = [];
          this.currentCampusId = '';
          this.profileForm.patchValue({ campusName: '' });
          this.loadCurrentBuildingDetails(payrollId);
        },
      })
    );
  }

  private parseCurrentCampusResponse(res: unknown): CampusOption[] {
    const candidates: Record<string, unknown>[] = [];
    const pushCandidate = (value: unknown) => {
      if (Array.isArray(value)) {
        value.forEach((item) => pushCandidate(item));
        return;
      }
      if (value && typeof value === 'object') {
        candidates.push(value as Record<string, unknown>);
      }
    };

    pushCandidate(res);
    for (const row of [...candidates]) {
      for (const key of [
        'data',
        'result',
        'response',
        'jsonV2',
        'currentCampus',
        'campus',
        'campusDetails',
        'campuses',
      ]) {
        pushCandidate(row[key]);
      }
    }

    const seen = new Set<string>();
    const options: CampusOption[] = [];
    for (const row of candidates) {
      const campusName = this.pick(row, 'campusName', 'name', 'campus');
      const campusId = this.pick(row, 'campusId', 'cmpId', 'id', 'campusID');
      if (!campusName && !campusId) continue;
      const key = `${campusId}|${campusName}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({ campusId, campusName: campusName || campusId });
    }
    return options;
  }

  /** GET currentBuildingByPayroll — set building input to current building. */
  private loadCurrentBuildingDetails(
    payrollId: string,
    opts?: { keepIfStale?: boolean; fallbackId?: string; fallbackLabel?: string }
  ): void {
    const fallback = this.readSavedBuilding(payrollId);
    const fallbackId = opts?.fallbackId || fallback.buildingId || this.originalBuildingId;
    const fallbackLabel =
      opts?.fallbackLabel || fallback.label || this.originalBuildingLabel;

    if (!payrollId) {
      if (fallbackLabel) {
        this.applyCurrentBuildingToForm(fallbackId, fallbackLabel, '');
      } else {
        this.profileForm.get('buildingName')?.setValue('', { emitEvent: false });
        this.originalBuildingId = '';
        this.originalBuildingLabel = '';
      }
      return;
    }

    this.subs.add(
      this.service.getCurrentBuildingDetails(payrollId).subscribe({
        next: (res) => {
          const current = this.parseCurrentBuildingResponse(res);
          const hasApiBuilding = !!(current.displayName || current.buildingId);

          // If GET is empty/stale right after save, keep the saved building on screen
          if (!hasApiBuilding && opts?.keepIfStale && fallbackLabel) {
            this.applyCurrentBuildingToForm(fallbackId, fallbackLabel, payrollId);
            return;
          }

          if (
            opts?.keepIfStale &&
            fallbackId &&
            current.buildingId &&
            String(current.buildingId) !== String(fallbackId)
          ) {
            // Backend still returning previous building — keep saved selection
            this.applyCurrentBuildingToForm(fallbackId, fallbackLabel, payrollId);
            return;
          }

          if (hasApiBuilding) {
            this.applyCurrentBuildingToForm(
              current.buildingId || fallbackId,
              current.displayName || fallbackLabel,
              payrollId
            );
            return;
          }

          if (fallbackLabel) {
            this.applyCurrentBuildingToForm(fallbackId, fallbackLabel, payrollId);
          } else {
            this.profileForm.get('buildingName')?.setValue('', { emitEvent: false });
            this.originalBuildingId = '';
            this.originalBuildingLabel = '';
          }
        },
        error: () => {
          if (fallbackLabel) {
            this.applyCurrentBuildingToForm(fallbackId, fallbackLabel, payrollId);
          }
        },
      })
    );
  }

  private parseCurrentBuildingResponse(
    res: unknown
  ): { buildingId: string; buildingName: string; displayName: string } {
    const candidates: Record<string, unknown>[] = [];
    const pushCandidate = (value: unknown) => {
      if (Array.isArray(value)) {
        value.forEach((item) => pushCandidate(item));
        return;
      }
      if (value && typeof value === 'object') {
        candidates.push(value as Record<string, unknown>);
      }
    };

    pushCandidate(res);
    for (const row of [...candidates]) {
      for (const key of [
        'data',
        'result',
        'response',
        'jsonV2',
        'currentBuilding',
        'building',
        'buildingDetails',
        'empBuilding',
      ]) {
        pushCandidate(row[key]);
      }
    }

    let buildingId = '';
    let buildingName = '';
    let fusion = '';

    for (const row of candidates) {
      if (!buildingId) {
        buildingId = this.pick(
          row,
          'buildingId',
          'changeBuildingId',
          'buildingID',
          'empBuildingId',
          'cmpBuildingId',
          'currentBuildingId'
        );
      }
      if (!buildingName) {
        buildingName = this.pick(
          row,
          'buildingName',
          'changeBuildingName',
          'changedBuildingName',
          'currentBuildingName',
          'buildingDesc',
          'empBuildingName',
          'name',
          'building'
        );
      }
      if (!fusion) {
        fusion = this.pick(
          row,
          'fusionBuildingCodeProd',
          'fusionBuilding',
          'fusionBuildingCode',
          'fusionCode'
        );
      }
      if (buildingId && buildingName) break;
    }

    const displayName =
      buildingName && fusion
        ? `${buildingName} - ${fusion}`
        : buildingName || fusion || '';

    return { buildingId, buildingName, displayName };
  }

  private loadBuildingsForCampusId(
    campusId: string,
    preferredBuildingName = '',
    preferredBuildingId = ''
  ): void {
    const keepName =
      preferredBuildingName ||
      String(this.profileForm.get('buildingName')?.value ?? '').trim();
    const keepId = preferredBuildingId || this.originalBuildingId;

    this.buildingList = [];
    this.filteredBuildingList = [];
    this.buildingSearch.setValue('', { emitEvent: false });
    if (!campusId) {
      this.loadingBuildings = false;
      this.spinner.hide();
      return;
    }

    this.subs.add(
      this.service.getBuildingDetails(campusId).pipe(
        finalize(() => {
          this.loadingBuildings = false;
          this.spinner.hide();
        })
      ).subscribe({
        next: (res) => {
          const mapped = this.asArray(res)
            .map((b) => this.toBuildingOption(b))
            .filter((b) => !!b.buildingId && !!b.label);
          const seen = new Set<string>();
          this.buildingList = mapped.filter((b) => {
            const key = b.buildingId || b.label;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          this.filteredBuildingList = [...this.buildingList];

          if (!this.buildingList.length) {
            this.buildingDropdownActive = false;
            this.hrAlert.show('No buildings found for this campus.', 'Error');
            return;
          }

          const selected = this.pickDefaultBuildingById(keepId, keepName);
          this.pendingBuildingLabel = selected.label;
          if (!this.originalBuildingLabel && selected.label) {
            this.originalBuildingLabel = selected.label;
          }
          if (!this.originalBuildingId && selected.buildingId) {
            this.originalBuildingId = selected.buildingId;
          }
          this.buildingSearch.setValue('', { emitEvent: false });
          this.filteredBuildingList = [...this.buildingList];
          this.buildingDropdownActive = true;
        },
        error: () => {
          this.buildingDropdownActive = false;
          this.pendingBuildingLabel = '';
          this.hrAlert.show('Unable to load buildings. Please try again.', 'Error');
        },
      })
    );
  }

  private pickDefaultBuildingById(
    savedBuildingId: string,
    savedBuildingName: string
  ): { buildingId: string; label: string } {
    if (savedBuildingId) {
      const byId = this.buildingList.find(
        (b) =>
          String(b.buildingId) === String(savedBuildingId) ||
          String(b.fusionCode) === String(savedBuildingId)
      );
      if (byId) return { buildingId: byId.buildingId, label: byId.label };
    }

    if (savedBuildingName) {
      const byName = this.findBuildingLabel(
        savedBuildingName,
        this.buildingList.map((b) => b.label)
      );
      const match = this.buildingList.find((b) => b.label === byName);
      if (match) return { buildingId: match.buildingId, label: match.label };
      return { buildingId: savedBuildingId, label: savedBuildingName };
    }

    const first = this.buildingList[0];
    return { buildingId: first?.buildingId ?? '', label: first?.label ?? '' };
  }

  private toBuildingOption(b: Record<string, unknown>): BuildingOption {
    return {
      buildingId: this.pick(b, 'buildingId', 'id', 'buildingID'),
      buildingName: this.pick(b, 'buildingName', 'name', 'building'),
      fusionCode: this.pick(
        b,
        'fusionBuildingCodeProd',
        'fusionBuilding',
        'fusionBuildingCode'
      ),
      label: this.formatBuildingLabel(b),
    };
  }

  private formatBuildingLabel(b: Record<string, unknown>): string {
    const name = this.pick(b, 'buildingName', 'name', 'building');
    const fusion = this.pick(
      b,
      'fusionBuildingCodeProd',
      'fusionBuilding',
      'fusionBuildingCode'
    );
    if (name && fusion) return `${name} - ${fusion}`;
    return name || fusion;
  }

  private findBuildingLabel(preferred: string, labels: string[]): string {
    if (!preferred) return '';
    if (labels.includes(preferred)) return preferred;
    return (
      labels.find(
        (label) =>
          label.startsWith(`${preferred} -`) || label.split(' - ')[0]?.trim() === preferred
      ) ?? ''
    );
  }

  private setControlEnabled(name: string, enabled: boolean): void {
    const ctrl = this.profileForm.get(name);
    enabled ? ctrl?.enable({ emitEvent: false }) : ctrl?.disable({ emitEvent: false });
  }

  private filterCampuses(search: string): void {
    const q = search.trim().toLowerCase();
    this.filteredCampusList = !q
      ? [...this.campusList]
      : this.campusList.filter((c) => c.campusName.toLowerCase().includes(q));
  }

  private filterBuildings(search: string): void {
    const q = search.trim().toLowerCase();
    this.filteredBuildingList = !q
      ? [...this.buildingList]
      : this.buildingList.filter((b) => b.label.toLowerCase().includes(q));
  }

  private resetCampuses(): void {
    this.campusList = [];
    this.filteredCampusList = [];
    this.campusSearch.setValue('', { emitEvent: false });
    this.resetBuildings();
  }

  private resetBuildings(): void {
    this.buildingList = [];
    this.filteredBuildingList = [];
    this.buildingSearch.setValue('', { emitEvent: false });
    this.buildingDropdownActive = false;
    this.loadingBuildings = false;
    this.currentCampusId = '';
    this.pendingBuildingLabel = '';
    this.originalBuildingId = '';
    this.originalBuildingLabel = '';
    this.setControlEnabled('buildingName', false);
    this.profileForm.get('buildingName')?.setValue('', { emitEvent: false });
  }

  private loadProfileImage(payrollId: string): void {
    this.revokeProfileImage();
    if (!payrollId) return;
    this.spinner.show();
    this.subs.add(
      this.service.getEmployeeImage(payrollId).pipe(
        finalize(() => this.spinner.hide())
      ).subscribe({
        next: (blob) => {
          if (!(blob instanceof Blob) || !blob.size || (blob.type && !blob.type.startsWith('image/'))) {
            return;
          }
          const url = URL.createObjectURL(blob);
          this.profileImageObjectUrl = url;
          this.profileImageUrl = url;
        },
        error: () => undefined,
      })
    );
  }

  private revokeProfileImage(): void {
    if (this.profileImageObjectUrl) {
      try {
        URL.revokeObjectURL(this.profileImageObjectUrl);
      } catch {
        /* ignore */
      }
    }
    this.profileImageObjectUrl = null;
    this.profileImageUrl = null;
  }

  private getSearchText(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'object' && !Array.isArray(value)) {
      return this.getLookupId(value as Record<string, unknown>);
    }
    const text = String(value).trim();
    return text.includes('|') ? text.split('|')[0]?.trim() || text : text;
  }

  private filterLocal(search: string): Record<string, unknown>[] {
    const q = search.toLowerCase();
    return this.optionsList.filter((item) =>
      [
        this.getLookupId(item),
        this.pick(item, 'empName', 'employeeName', 'userName', 'name'),
        this.pick(item, 'mobileNo', 'mobileNumber', 'mobile'),
        this.pick(item, 'empId', 'employeeId'),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }

  private getLookupId(data: Record<string, unknown>): string {
    return (
      this.pick(data, 'payrollId', 'payRollId', 'payroll') ||
      this.pick(data, 'employeeId', 'empId')
    );
  }

  private asArray(res: unknown): Record<string, unknown>[] {
    if (Array.isArray(res)) return res.filter((x) => x && typeof x === 'object') as Record<string, unknown>[];
    if (!res || typeof res !== 'object') return [];
    const root = res as Record<string, unknown>;
    const data = root['data'] ?? root['result'] ?? root['buildings'] ?? root['response'];
    return Array.isArray(data) ? (data.filter((x) => x && typeof x === 'object') as Record<string, unknown>[]) : [];
  }

  private unwrapEmpDetails(res: unknown): Record<string, unknown> {
    let cur: unknown = res;
    if (Array.isArray(cur)) cur = cur[0];
    if (!cur || typeof cur !== 'object') return {};

    let row = cur as Record<string, unknown>;
    for (const key of [
      'data',
      'result',
      'jsonV2',
      'empDetails',
      'employeeDetails',
      'response',
      'body',
    ]) {
      const inner = row[key];
      if (Array.isArray(inner) && inner[0] && typeof inner[0] === 'object') {
        row = inner[0] as Record<string, unknown>;
        break;
      }
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        row = inner as Record<string, unknown>;
        break;
      }
    }
    return row;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  /** Show only last 4 digits; mask the rest (e.g. ******3210). */
  private maskMobileNumber(value: unknown): string {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length <= 4) return digits;
    return '*'.repeat(digits.length - 4) + digits.slice(-4);
  }

  private pick(item: Record<string, unknown>, ...keys: string[]): string {
    const usable = (val: unknown): string => {
      if (val == null) return '';
      const text = String(val).trim();
      if (!text || text === '0' || text === '-' || text.toLowerCase() === 'null') return '';
      return text;
    };

    for (const key of keys) {
      const text = usable(item[key]);
      if (text) return text;
    }
    const lowerMap = new Map(
      Object.keys(item).map((k) => [k.toLowerCase(), item[k]] as const)
    );
    for (const key of keys) {
      const text = usable(lowerMap.get(key.toLowerCase()));
      if (text) return text;
    }
    return '';
  }
}
